/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useEffect, useState } from 'react';

import { cssMap as customCssMap } from '@compiled/react';
import { bind } from 'bind-event-listener';

import { AtlassianDesignLogo, ThemeControls } from '@af/design-system-docs-ui';
import { getDocument } from '@atlaskit/browser-apis';
import Button from '@atlaskit/button/default/button';
import IconButton from '@atlaskit/button/icon/button';
import LinkButton from '@atlaskit/button/link';
import { cssMap, jsx } from '@atlaskit/css';
import SearchIcon from '@atlaskit/icon/core/search';
import SidebarCollapseIcon from '@atlaskit/icon/core/sidebar-collapse';
import SidebarExpandIcon from '@atlaskit/icon/core/sidebar-expand';
import { useToggleSideNav } from '@atlaskit/navigation-system';
import { TopNav } from '@atlaskit/navigation-system/layout/top-nav';
import { Anchor, Box, Inline, UNSAFE_useMediaQuery } from '@atlaskit/primitives/compiled';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- TODO: migrate to @atlaskit/primitives/compiled
import { Show } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { ButtonTextWrapper } from './utils/button-text-wrapper';
import { NavIconWrapper } from './utils/nav-icon-wrapper';

const getWindow = (): Window | null => {
	if (typeof window !== 'undefined') {
		return window;
	}
	return null;
};

const navStyles = cssMap({
	root: {
		position: 'sticky',
		insetBlockStart: 0,
		width: '100%',
		backgroundColor: token('elevation.surface'),
		minHeight: '72px',
		display: 'flex',
		alignItems: 'center',
		boxSizing: 'border-box',
		gridArea: 'top-bar',
		maxHeight: 'fit-content',
		borderColor: 'transparent',
	},
	contentContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		width: '100%',
	},
	contentContainerGrid: {
		gridColumn: '1 / -1',
		display: 'grid',
		gridTemplateColumns: 'auto 1fr auto',
		alignItems: 'center',
		width: '100%',
		gap: token('space.100'),
		paddingBlockStart: token('space.025'),
		paddingInline: token('space.050'),
		'@media (min-width: 48rem)': {
			gridTemplateColumns: '1fr minmax(min-content, max-content) 1fr',
		},
		'@media (min-width: 64rem)': {
			paddingInline: token('space.250'),
		},
	},
	contentContainerNoBorder: {
		borderBlockEndStyle: 'none',
	},
	startSection: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-start',
	},
	endSection: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
		gap: token('space.050'),
		'@media (min-width: 48rem)': {
			gap: token('space.200'),
		},
		'@media (min-width: 64rem)': {
			gap: token('space.400'),
		},
	},
	logoContainer: {
		width: 'auto',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-start',
		position: 'relative',
	},
	linkBorderWrapper: {
		borderBlockEndStyle: 'solid',
		borderBlockEndColor: 'transparent',
	},
	linkBorderWrapperActive: {
		borderBlockEndColor: token('color.border.bold'),
	},
	noBorder: {
		borderBlockEndStyle: 'none',
	},
	bottomBorder: {
		borderBlockEndColor: token('color.border'),
		borderBlockEndStyle: 'solid',
		borderBlockEndWidth: token('border.width'),
	},
	logoAnchor: {
		display: 'flex',
		alignItems: 'center',
		paddingBlock: token('space.100'),
		minWidth: '32px',
		maxHeight: '32px',
		borderRadius: token('radius.small', '3px'),
		justifyContent: 'center',
		'@media (min-width: 30rem)': {
			paddingInline: token('space.100'),
		},
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
});

// Match the z-index from navigation-system's localSlotLayers.topBar (value: 4)
// This ensures proper layering: top nav (4) above side nav (2)
const customNavStyles = customCssMap({
	rootZIndex: {
		zIndex: 4,
	},
	containerPadding: {
		paddingInlineStart: token('space.200'),
		paddingInlineEnd: `calc(${token('space.200')} - var(--scrollbar-width))`,
		'@media (min-width: 64rem)': {
			paddingInlineStart: token('space.400'),
			paddingInlineEnd: `calc(${token('space.400')} - var(--scrollbar-width))`,
		},
	},
	linkBorderWrapper: {
		borderBlockEndWidth: '2px',
	},
	backdrop: {
		backgroundColor: 'var(--nav-backdrop-color, rgba(255, 255, 255, 0.85))',
		backdropFilter: 'blur(10px)',
		WebkitBackdropFilter: 'blur(10px)',
		'@media (prefers-reduced-transparency: reduce)': {
			backgroundColor: token('elevation.surface'),
			backdropFilter: 'none',
			WebkitBackdropFilter: 'none',
		},
	},
	/**
	 * The text colors `@atlaskit/logo` uses, read by `ButtonTextWrapper` and `NavIconWrapper`. The
	 * light/dark swap is in CSS, keyed on the `data-color-mode` the pre-paint script has already set
	 * on `<html>`, so the prerendered markup is identical for every reader. Setting the variable from
	 * JS made the server emit one value and a dark reader's first client render another, a hydration
	 * mismatch React 18 does not patch.
	 */
	textColor: {
		'--nav-text-color': '#101214',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- swaps the color by color mode without re-rendering
		'html[data-color-mode="dark"] &': {
			'--nav-text-color': '#E2E3E4',
		},
	},
});

/**
 * __MobileSideNavToggleButton__
 *
 * Button for toggling the side nav on mobile devices.
 * Matches the design of the standard navigation-system toggle button.
 * Uses the isExpanded prop which is kept in sync via onExpand/onCollapse callbacks.
 * Stops event propagation to prevent click-outside handler from collapsing the nav.
 */
const MobileSideNavToggleButton = ({ isExpanded }: { isExpanded?: boolean }) => {
	const toggleSideNav = useToggleSideNav({ trigger: 'toggle-button' });

	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		// Stop propagation to prevent click-outside handler from collapsing the nav
		event.stopPropagation();
		toggleSideNav();
	};

	const CollapseIcon = (props: React.ComponentProps<typeof SidebarCollapseIcon>) => {
		const { color: _color, ...restProps } = props as React.ComponentProps<
			typeof SidebarCollapseIcon
		> & { color?: string };
		return (
			<NavIconWrapper>
				<SidebarCollapseIcon {...restProps} />
			</NavIconWrapper>
		);
	};
	const ExpandIcon = (props: React.ComponentProps<typeof SidebarExpandIcon>) => {
		const { color: _color, ...restProps } = props as React.ComponentProps<
			typeof SidebarExpandIcon
		> & { color?: string };
		return (
			<NavIconWrapper>
				<SidebarExpandIcon {...restProps} />
			</NavIconWrapper>
		);
	};

	return (
		<IconButton
			appearance="subtle"
			label={isExpanded ? 'Collapse side navigation' : 'Expand side navigation'}
			icon={isExpanded ? CollapseIcon : ExpandIcon}
			onClick={handleClick}
			// `IconButton` disables its tooltip by default. The button is icon-only at these
			// breakpoints, so the tooltip is the only visible affordance describing what it does.
			isTooltipDisabled={false}
			testId="mobile-side-nav-toggle"
			tooltip={{ hasNewContentOnTriggerClick: true }}
		/>
	);
};

let storedScrollbarWidth = 0;

/**
 * Top navigation__
 *
 * A custom top navigation for the Atlassian Design System website.
 */
export const TopNavigation: ({
	id,
	onSearchOpen,
	pathname,
	isBottomBorderVisible,
	shouldUsePageLayout,
	themeSwitcher,
	isSideNavExpanded,
}: {
	id?: string;
	onSearchOpen: () => void;
	pathname?: string;
	isBottomBorderVisible?: boolean;
	themeSwitcher?: React.ReactNode;
	shouldUsePageLayout?: boolean;
	isSideNavExpanded?: boolean;
}) => JSX.Element = ({
	id,
	onSearchOpen,
	pathname = '',
	isBottomBorderVisible = true,
	shouldUsePageLayout = false,
	themeSwitcher,
	isSideNavExpanded,
}: {
	id?: string;
	onSearchOpen: () => void;
	pathname?: string;
	isBottomBorderVisible?: boolean;
	themeSwitcher?: React.ReactNode;
	shouldUsePageLayout?: boolean;
	isSideNavExpanded?: boolean;
}) => {
	// Normalize pathname to handle trailing slashes
	const normalizedPath = pathname.replace(/\/$/, '') || '/';
	const isNewsActive = normalizedPath.startsWith('/news');
	const isHomepage = normalizedPath === '/';
	const isDesignSystemActive = !isNewsActive && !isHomepage;
	const isBelowMd = UNSAFE_useMediaQuery('below.md')?.matches ?? false;

	const [scrollbarWidth, setScrollbarWidth] = useState(storedScrollbarWidth);

	useEffect(() => {
		if (!isHomepage) {
			setScrollbarWidth(storedScrollbarWidth);
			return;
		}

		const calculateScrollbarWidth = () => {
			const width = (getWindow()?.innerWidth ?? 0) - (getDocument()?.body?.scrollWidth ?? 0);
			storedScrollbarWidth = width;
			setScrollbarWidth(width);
		};

		if (typeof window !== 'undefined') {
			if (document.readyState === 'complete') {
				calculateScrollbarWidth();
			} else {
				return bind(window, {
					type: 'load',
					listener: calculateScrollbarWidth,
				});
			}
		}
	}, [isHomepage]);

	// Shared navigation content - used by both homepage and non-homepage
	const renderNavContent = () => {
		const startContent = (
			<div css={navStyles.startSection}>
				<Inline space="space.100" alignInline="center">
					{isDesignSystemActive && (
						<Show below="md">
							<MobileSideNavToggleButton isExpanded={isSideNavExpanded} />
						</Show>
					)}
					<Box xcss={navStyles.logoContainer}>
						<Anchor href="/" aria-label="Atlassian Design System home" xcss={navStyles.logoAnchor}>
							<AtlassianDesignLogo />
						</Anchor>
					</Box>
				</Inline>
			</div>
		);

		const endContent = (
			<div css={navStyles.endSection}>
				<div
					css={[
						navStyles.linkBorderWrapper,
						customNavStyles.linkBorderWrapper,
						isDesignSystemActive && navStyles.linkBorderWrapperActive,
					]}
				>
					<LinkButton appearance="subtle" href="/design-system">
						<ButtonTextWrapper>Design system</ButtonTextWrapper>
					</LinkButton>
				</div>
				<div
					css={[
						navStyles.linkBorderWrapper,
						customNavStyles.linkBorderWrapper,
						isNewsActive && navStyles.linkBorderWrapperActive,
					]}
				>
					<LinkButton appearance="subtle" href="/news">
						<ButtonTextWrapper>News</ButtonTextWrapper>
					</LinkButton>
				</div>
				<Show below="sm">
					<IconButton
						icon={(props) => {
							const { color: _color, ...restProps } = props as React.ComponentProps<
								typeof SearchIcon
							> & { color?: string };
							return (
								<NavIconWrapper>
									<SearchIcon {...restProps} />
								</NavIconWrapper>
							);
						}}
						appearance="subtle"
						label="Search"
						testId="search"
						onClick={onSearchOpen}
					/>
				</Show>
				<Show above="sm">
					<Button
						iconBefore={(props) => {
							const { color: _color, ...restProps } = props as React.ComponentProps<
								typeof SearchIcon
							> & { color?: string };
							return (
								<NavIconWrapper>
									<SearchIcon {...restProps} />
								</NavIconWrapper>
							);
						}}
						appearance="subtle"
						onClick={onSearchOpen}
					>
						<ButtonTextWrapper>Search</ButtonTextWrapper>
					</Button>
				</Show>
				{themeSwitcher ?? <ThemeControls />}
			</div>
		);

		return { startContent, endContent };
	};

	const { startContent, endContent } = renderNavContent();

	// When shouldUsePageLayout is true (design system pages), use TopNav from navigation-system
	if (shouldUsePageLayout) {
		return (
			<TopNav id={id} testId={id} height={71}>
				<div
					css={[
						navStyles.contentContainerGrid,
						!isBottomBorderVisible && navStyles.contentContainerNoBorder,
						customNavStyles.backdrop,
						customNavStyles.textColor,
					]}
				>
					{startContent}
					<div />
					{endContent}
				</div>
			</TopNav>
		);
	}

	// When shouldUsePageLayout is false (homepage and news), use the custom header - this allows for the blurred background behind the header
	return (
		<header
			id={id}
			css={[
				navStyles.root,
				customNavStyles.rootZIndex,
				customNavStyles.backdrop,
				customNavStyles.textColor,
				isBottomBorderVisible ? navStyles.bottomBorder : navStyles.noBorder,
			]}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Dynamic CSS variable based on homepage and breakpoint
			style={
				{
					'--scrollbar-width': isBelowMd ? '0px' : `${scrollbarWidth}px`,
				} as React.CSSProperties
			}
			data-layout-slot="true"
		>
			<nav css={[navStyles.contentContainer, customNavStyles.containerPadding]}>
				<div css={customNavStyles.rootZIndex}>{startContent}</div>
				{endContent}
			</nav>
		</header>
	);
};
